'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useCallback, useRef, ReactNode, useMemo } from 'react'

import AContentCardHeader from '@/_components/custom/cards/content-card/components/a-content-card-header'
import { useSectionParams } from '@/_i18n/section-params'
import { IAnnualProgram } from '@/_types'
import { getAnnualProgramItemCategory2, pushAddToCart } from '@/_utils/analytics/datalayer'

import { useAPDetailVisibility } from '../../_hooks'

import ZAPDetailButton from './z-ap-detail-button'

/**
 * Props for the ZAPDetailProgramDetail component.
 */
interface IZAPDetailProgramDetailProps {
	/** Primary category/gender for styling ('male' or 'female') */
	primaryCategory: 'male' | 'female' | 'child'
	/** Annual program data to display */
	annualProgram: IAnnualProgram
	/** Program detail content component to render */
	children: ReactNode
	/** Whether to show the "Add optional modules" buttons (defaults to false) */
	hasModules?: boolean
	/** Text for the order button (defaults to 'Objednať') */
	orderButtonText?: string
}

/**
 * Universal program detail component for all annual program pages.
 * Displays complete program information including:
 * - Program header with booking links
 * - Detailed program benefits and content (via children)
 * - Action buttons (desktop and mobile sticky)
 * - Mobile sticky footer with CTAs
 *
 * Uses Intersection Observer to show/hide mobile sticky footer based on visibility.
 * Shared across Medante Balance, Mini, and Vital programs.
 *
 * @param primaryCategory - Gender category for styling ('male' = blue, 'female' = cyclamen)
 * @param annualProgram - Program data with benefits, pricing, and details
 * @param children - Program detail content component (specific to each program)
 * @param orderButtonText - Text for the order button (defaults to 'Objednať')
 *
 * @example
 * ```typescript
 * <ZAPDetailProgramDetail
 *   primaryCategory='female'
 *   annualProgram={femaleProgram}
 * >
 *   <AMBProgramDetailContent program={femaleProgram} />
 * </ZAPDetailProgramDetail>
 * ```
 */
export default function ZAPDetailProgramDetail({
	primaryCategory,
	annualProgram,
	children,
	hasModules = false,
	orderButtonText = 'Objednať'
}: IZAPDetailProgramDetailProps) {
	const { getSectionParams } = useSectionParams('annualPrograms')
	const sectionRef = useRef<HTMLElement>(null)
	const isVisible = useAPDetailVisibility(sectionRef)

	const handleOrderClick = useCallback(() => {
		pushAddToCart(
			{
				id: annualProgram.id,
				name: annualProgram.name,
				type: 'annual-program',
				price: annualProgram.price,
				priceOld: annualProgram.priceOld
			},
			{ item_category2: getAnnualProgramItemCategory2({ primaryCategory }) }
		)
	}, [annualProgram, primaryCategory])

	const categoryMap = useMemo(
		() => ({
			male: {
				wrapperClassName: 'medante-vital__program-detail--male',
				articleClassName: 'item_complex-health_blue',
				orderButtonClassName: 'white--blue',
				moduleButtonClassName: 'white-outline--blue',
				addModulesButtonClassName: 'blue-outline',
				detailButtonClassName: 'blue'
			},
			female: {
				wrapperClassName: 'medante-vital__program-detail--female',
				articleClassName: 'item_complex-health_cyclamen',
				orderButtonClassName: 'white--cyclamen',
				moduleButtonClassName: 'white-outline--cyclamen',
				addModulesButtonClassName: 'cyclamen-outline',
				detailButtonClassName: 'cyclamen'
			},
			child: {
				wrapperClassName: 'medante-vital__program-detail--child',
				articleClassName: 'item_complex-health_child',
				orderButtonClassName: 'white--child',
				moduleButtonClassName: 'white-outline--child',
				addModulesButtonClassName: 'child-outline',
				detailButtonClassName: 'sand'
			}
		}),
		[]
	)

	const currentCategory = useMemo(
		() => categoryMap[primaryCategory],
		[primaryCategory, categoryMap]
	)

	return (
		<section
			ref={sectionRef}
			className={clsx('medante-vital__program-detail', currentCategory.wrapperClassName)}
			id='detail-programu'
		>
			<div className='wrapper2 pdt-60lg pdt-0 pdb-80 pdb-40lg relative'>
				<div
					key={annualProgram.id}
					id={`${getSectionParams('annualProgramDetail')}-${annualProgram.id}`}
					className={clsx('w-full mb-5')}
				>
					<article
						className={clsx(
							'item_complex-health',
							currentCategory.articleClassName,
							'border-b-4 !border-none !shadow-none'
						)}
					>
						<div className='item_complex-health_inner'>
							<div className='item_complex-health_content'>
								<AContentCardHeader
									program={annualProgram}
									subtitle='Ročný program'
									description={annualProgram.cardHeaderDescription}
									headerRight={
										<div className='hidden md:flex justify-between gap-2'>
										<Link
											className={clsx(
												'btn_basic white',
												currentCategory.orderButtonClassName
											)}
											href={`/volne-terminy?id=${annualProgram.id}&type=annual-program`}
											onClick={handleOrderClick}
										>
												<span className='!font-medium !whitespace-nowrap'>
													Objednať ročný program
												</span>
											</Link>

											{hasModules && (
												<Link
													className={clsx(
														'btn_basic white-outline',
														currentCategory.moduleButtonClassName
													)}
													href='#kupit-program'
												>
													<span className='!font-medium !whitespace-nowrap'>
														Pridať voliteľné moduly
													</span>
												</Link>
											)}
										</div>
									}
								/>

								<div className='w-full bg-[#FCFAF6]'>{children}</div>
							</div>
						</div>
					</article>

				{/* Desktop Action Buttons */}
				<div className='flex items-center flex-col md:flex-row justify-center gap-4 md:gap-6 border-t border-gray-200 pt-[44px]'>
					<ZAPDetailButton
						text={orderButtonText}
						href={`/volne-terminy?id=${annualProgram.id}&type=annual-program`}
						onClick={handleOrderClick}
						isPrimary={true}
						color={
							currentCategory.detailButtonClassName as
								| 'blue'
								| 'cyclamen'
								| 'child'
						}
						price={annualProgram.price}
						priceOld={annualProgram.priceOld}
						className='!min-w-auto'
					/>

						{hasModules && (
							<Link
								title='Pridať voliteľné moduly'
								className={clsx(
									'btn_basic medante-vital__button min-w-auto!',
									currentCategory.addModulesButtonClassName
								)}
								style={{ textDecoration: 'none' }}
								href='#kupit-program'
							>
								<span>Pridať voliteľné moduly</span>
							</Link>
						)}
					</div>
				</div>

				{/* Mobile Sticky Footer */}
				<div
					className={clsx(
						'fixed bottom-0 left-0 right-0 bg-white z-100 bg-[#fcfaf6] block md:hidden transition-transform duration-500 border-t border-gray-200',
						isVisible ? 'translate-y-0' : 'translate-y-full'
					)}
					style={{
						transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)'
					}}
				>
				<div className='p-3 flex flex-col items-center justify-between gap-3'>
					<ZAPDetailButton
						text={orderButtonText}
						href={`/volne-terminy?id=${annualProgram.id}&type=annual-program`}
						onClick={handleOrderClick}
						isPrimary={true}
						color={
							currentCategory.detailButtonClassName as
								| 'blue'
								| 'cyclamen'
								| 'child'
						}
						price={annualProgram.price}
						priceOld={annualProgram.priceOld}
						className='!min-w-[220px]'
					/>

						{hasModules && (
							<Link
								title='Pridať voliteľné moduly'
								className={clsx(
									'btn_basic medante-vital__button !min-w-[220px]',
									currentCategory.addModulesButtonClassName
								)}
								style={{ textDecoration: 'none' }}
								href='#kupit-program'
							>
								<span>Pridať voliteľné moduly</span>
							</Link>
						)}
					</div>
				</div>
			</div>
		</section>
	)
}
